import { useEffect, useRef, useState } from "react";
import type { Product } from "../lib/types";
import { type CartItem, useCart } from "../context/cartContext";
import { useFavorites } from "../context/favoriteContext";

import { ExternalLink, Heart, Image, ShoppingCart } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import {
  formatPrice,
  sellingPrice,
  slugify,
} from "../lib/helpers/storehelpers";
import { isProductMediaImageFileName } from "../lib/supabase";
import { StarRating } from "./StarRating";
import { Link } from "@remix-run/react";
import Button from "./theme/Button";
import { ImageWithFallback } from "./ImageWithFallback";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

function priceAfterOrderClassName(size: "lg" | "sm" = "lg") {
  return size === "lg"
    ? "text-base sm:text-lg font-normal text-gray-500"
    : "text-sm font-normal text-gray-500";
}

// Helper function for consistent price display
function PriceDisplay({
  price,
  markdown,
}: {
  price: number | null;
  markdown?: number | null;
}) {
  const selling = sellingPrice(price, markdown);

  if (selling === null) {
    return (
      <div className="mb-4 min-h-[32px] flex items-center">
        <span className="text-sm text-gray-400">Price not available</span>
      </div>
    );
  }

  if (selling === 0) {
    return (
      <div className="mb-4 min-h-[32px] flex items-center">
        <span className={priceAfterOrderClassName("lg")}>
          Contact Supplier for Pricing
        </span>
      </div>
    );
  }

  const showSale = markdown != null &&
    price != null &&
    markdown > 0 &&
    price > markdown;

  if (showSale) {
    return (
      <div className="mb-4 min-h-[32px] flex items-center">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xl sm:text-2xl font-bold text-red-600">
            ${formatPrice(markdown!)}
          </span>
          <span className="text-base sm:text-lg text-gray-500 line-through">
            ${formatPrice(price)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 min-h-[32px] flex items-center">
      <span className="text-xl sm:text-2xl font-bold text-gray-900">
        ${formatPrice(selling)}
      </span>
    </div>
  );
}

type ProductCardSize = "mini" | "full" | "thumbnail" | "accordion" | "promo";

interface IProps {
  productId: string;
  productDetails?: Product;
  size?: ProductCardSize;
  className?: string;
  disableCart?: boolean;
  storeName?: string;
  expanded?: boolean; // For accordion size
  fluid?: boolean;
}

export function ProductCard(props: IProps) {
  const {
    productId,
    productDetails: initialProductDetails,
    size = "full",
    className = "",
    disableCart = false,
    expanded = false,
    fluid = false,
  } = props;

  const [productDetails, setProductDetails] = useState<Product | undefined>(
    initialProductDetails,
  );
  const [mainImageUrl, setMainImageUrl] = useState<string | undefined>();
  const [storeName, setStoreName] = useState<string | undefined>(
    props.storeName,
  );
  const [isLoading, setIsLoading] = useState(!initialProductDetails);
  const { addToCart, canAddStoreProduct } = useCart();
  const cartDisabled =
    disableCart ||
    (productDetails?.store_id != null &&
      !canAddStoreProduct(productDetails.store_id));

  useEffect(() => {
    const fetchProductData = async () => {
      setIsLoading(true);

      try {
        // Fetch product if not provided
        let product = productDetails;

        if (initialProductDetails) {
          setProductDetails(initialProductDetails);
        }

        if (!product) {
          try {
            const response = await fetch(`/api/products/${productId}`);
            if (response.ok) {
              const data = await response.json();
              if (data.product) {
                product = data.product as Product;
              }
            } else {
              console.error("Error fetching product:", response.statusText);
            }
          } catch (error) {
            console.error("Error fetching product:", error);
            return;
          }

          if (!product) return;
          setProductDetails(product);
        }

        // Use main_image from database if it exists and is valid, otherwise find first image
        setMainImageUrl((currentUrl) => {
          // First, check if product.main_image exists and is still in media_urls
          if (
            product.main_image &&
            Object.values(product.media_urls || {}).includes(product.main_image)
          ) {
            return product.main_image;
          }

          // If current mainImageUrl still exists in media_urls, keep it
          if (
            currentUrl &&
            Object.values(product.media_urls || {}).includes(currentUrl)
          ) {
            return currentUrl;
          }

          // Otherwise, find the first image following the order in media_urls
          const firstImage = Object.keys(product.media_urls || {}).find(
            (image) => isProductMediaImageFileName(image),
          );

          if (firstImage && product.media_urls?.[firstImage]) {
            return product.media_urls[firstImage];
          } else {
            return undefined;
          }
        });

        if (props.storeName) {
          setStoreName(props.storeName);
        } else {
          try {
            const response = await fetch(`/api/store/${product.store_id}`);
            if (response.ok) {
              const data = await response.json();
              if (data.store && data.store.name) {
                setStoreName(data.store.name);
              }
            } else {
              console.error("Error fetching store:", response.statusText);
            }
          } catch (error) {
            console.error("Error fetching store:", error);
          }
        }
      } catch (error) {
        console.error("Error in product data fetching:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductData();
  }, [
    productId,
    props.storeName,
    initialProductDetails?.name,
    initialProductDetails?.description,
    initialProductDetails?.average_rating,
    initialProductDetails?.price,
    initialProductDetails?.markdown,
  ]);

  useEffect(() => {
    if (size !== "accordion" && initialProductDetails?.media_urls) {
      // Use main_image from database if it exists and is valid, otherwise find first image
      setMainImageUrl((currentUrl) => {
        // First, check if product.main_image exists and is still in media_urls
        if (
          initialProductDetails.main_image &&
          Object.values(initialProductDetails.media_urls || {}).includes(
            initialProductDetails.main_image,
          )
        ) {
          return initialProductDetails.main_image;
        }

        // If current mainImageUrl still exists in media_urls, keep it
        if (
          currentUrl &&
          Object.values(initialProductDetails.media_urls || {}).includes(
            currentUrl,
          )
        ) {
          return currentUrl;
        }

        // Otherwise, find the first image following the order in media_urls
        const firstImage = Object.keys(initialProductDetails.media_urls || {})
          .find((image) => isProductMediaImageFileName(image));

        if (firstImage && initialProductDetails.media_urls?.[firstImage]) {
          return initialProductDetails.media_urls[firstImage];
        } else {
          return undefined;
        }
      });
    }
  }, [
    initialProductDetails?.media_urls,
    initialProductDetails?.main_image,
    size,
  ]);

  // Early return loading skeleton based on size
  if (isLoading) {
    switch (size) {
      case "thumbnail":
        return <ProductCardThumbnailSkeleton />;
      case "accordion":
        return <ProductCardAccordionSkeleton />;
      case "mini":
        return <ProductCardMiniSkeleton />;
      case "promo":
        return <ProductCardPromoSkeleton fluid={fluid} />;
      default:
        return <ProductCardFullSkeleton />;
    }
  }

  // Product unavailable (deleted, inactive store, etc.)
  if (!productDetails) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center opacity-60">
        <Image className="h-8 w-8 text-gray-300 mb-2" />
        <span className="text-xs text-gray-400">Product unavailable</span>
      </div>
    );
  }

  // Render appropriate component based on size
  switch (size) {
    case "thumbnail":
      return (
        <ProductCardThumbnail
          productDetails={productDetails}
          mainImageUrl={mainImageUrl}
          className={className}
        />
      );
    case "accordion":
      return (
        <ProductCardAccordion
          productDetails={productDetails}
          storeName={storeName}
          className={className}
          disableCart={cartDisabled}
          expanded={expanded}
          addToCart={addToCart}
        />
      );
    case "mini":
      return (
        <ProductCardMini
          productDetails={productDetails}
          mainImageUrl={mainImageUrl}
          storeName={storeName}
          className={className}
          disableCart={cartDisabled}
          addToCart={addToCart}
        />
      );
    case "promo":
      return (
        <ProductCardPromo
          productDetails={productDetails}
          mainImageUrl={mainImageUrl}
          storeName={storeName}
          className={className}
          fluid={fluid}
        />
      );
    default:
      return (
        <ProductCardFull
          productDetails={productDetails}
          mainImageUrl={mainImageUrl}
          storeName={storeName}
          className={className}
          disableCart={cartDisabled}
          addToCart={addToCart}
        />
      );
  }
}

// Skeleton Components
function ProductCardFullSkeleton() {
  return (
    <div className="w-full max-w-sm h-[420px] bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden flex flex-col">
      <Skeleton className="w-full h-[250px] flex-shrink-0" />
      <div className="p-4 flex flex-col flex-1 min-h-0">
        <Skeleton className="h-12 w-full mb-4" />
        <Skeleton className="h-8 w-24 mb-4" />
        <Skeleton className="h-10 w-full rounded-md mt-auto" />
      </div>
    </div>
  );
}

function ProductCardThumbnailSkeleton() {
  return (
    <div className="rounded-lg overflow-hidden w-[100px] h-[100px]">
      <Skeleton className="w-full h-full" />
    </div>
  );
}

function ProductCardAccordionSkeleton() {
  return (
    <div className="border rounded-md p-4">
      <div className="flex justify-between items-center">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="mt-2">
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  );
}

function ProductCardMiniSkeleton() {
  return (
    <div className="w-full border rounded-md h-full flex flex-col shadow-md">
      {/* Header */}
      <div className="px-4 py-3 border-b">
        <div className="flex justify-between w-full items-start gap-2 min-w-0">
          {/* Product name and store */}
          <div className="text-left flex-1 min-w-0 overflow-hidden">
            <Skeleton className="h-4 w-3/4 mb-1" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          {/* Price on the right */}
          <div className="text-right flex-shrink-0 ml-2">
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3 flex-1 flex flex-col">
        {/* Rating */}
        <div className="flex items-center gap-1 mb-3 flex-wrap">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-8" />
        </div>

        {/* Description */}
        <div className="mt-3 mb-4">
          <Skeleton className="h-3 w-full mb-1" />
          <Skeleton className="h-3 w-2/3 mb-1" />
          <Skeleton className="h-3 w-1/2" />
        </div>

        {/* Content area that expands */}
        <div className="flex-1 flex flex-col">
          {/* Price display */}
          <div className="mb-3">
            <Skeleton className="h-6 w-20" />
          </div>
        </div>

        {/* Action buttons - pushed to bottom */}
        <div className="flex flex-col sm:flex-row gap-2 mt-auto">
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
    </div>
  );
}

function ProductCardPromoSkeleton({ fluid }: { fluid?: boolean }) {
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-gray-50 overflow-hidden hover:shadow-lg transition-shadow ${
        fluid
          ? "flex h-full min-h-0 w-full max-w-full min-w-0 flex-col"
          : "flex h-full min-h-0 w-[200px] min-w-[200px] shrink-0 flex-col"
      }`}
    >
      <div className="relative h-32 shrink-0 bg-gray-100">
        <Skeleton className="h-32 w-full shrink-0" />
        <Skeleton className="absolute top-2 right-2 h-5 w-5 rounded-full" />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3">
        <Skeleton className="mb-1 h-4 w-3/4" />
        <Skeleton className="mb-2 h-3 w-1/2" />
        <Skeleton className="mt-auto h-4 w-16" />
      </div>
    </div>
  );
}

// Card Variants
interface CardProps {
  productDetails: Product;
  mainImageUrl?: string;
  storeName?: string;
  className?: string;
  disableCart?: boolean;
  expanded?: boolean;
  fluid?: boolean;
  addToCart?: (item: CartItem) => void;
}

function ProductCardThumbnail({
  productDetails,
  mainImageUrl,
  storeName,
  className,
}: CardProps) {
  if (!storeName) {
    return <ProductCardThumbnailSkeleton />;
  }

  const productUrl = `/store/${
    slugify(storeName)
  }/products?product=${productDetails.id}`;

  const imageFallback = (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <Image
        className="w-6 h-6 text-gray-400"
        aria-label="No image available"
      />
    </div>
  );

  return (
    <Link
      to={productUrl}
      className={`block rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow w-[100px] h-[100px] ${className}`}
    >
      <div className="w-full h-full">
        {mainImageUrl
          ? (
            <ImageWithFallback
              key={mainImageUrl}
              src={mainImageUrl}
              alt={productDetails.name || "Product"}
              className="w-full h-full object-contain"
              fallback={imageFallback}
            />
          )
          : imageFallback}
      </div>
    </Link>
  );
}

function ProductCardAccordion({
  productDetails,
  storeName,
  className,
  disableCart,
  expanded = false,
  addToCart,
}: CardProps) {
  const defaultValue = expanded ? "item-1" : undefined;
  const productName = productDetails.name || "Unnamed Product";

  if (!storeName) {
    return <ProductCardAccordionSkeleton />;
  }

  return (
    <Accordion
      type="single"
      collapsible
      className={`w-full border rounded-md min-h-[60px] ${className}`}
      defaultValue={defaultValue}
    >
      <AccordionItem
        value="item-1"
        className="shadow-md hover:shadow-lg  transition-shadow duration-300"
      >
        <AccordionTrigger className="px-4 py-3 hover:no-underline [&>svg]:ml-4 w-full">
          <div className="flex justify-between w-full items-start gap-2 min-w-0">
            {/* Left side: Favorite button and product name */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Favorite button */}
              {
                /* <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-transparent hover:bg-transparent flex-shrink-0"
                onClick={() => {
                  e.stopPropagation();
                  addToFavorites(productDetails.id);
                }}
              >
                <Heart className="h-4 w-4" />
              </Button> */
              }

              {/* Product name and store */}
              <div className="text-left flex-1 min-w-0 overflow-hidden">
                <h3 className="font-medium text-sm truncate">
                  {productName}
                  {productDetails.average_rating && (
                    <span className="text-xs font-normal text-gray-500 ml-1">
                      ({productDetails.average_rating.toFixed(1)})
                    </span>
                  )}
                </h3>
                {storeName && (
                  <p className="text-xs text-gray-500 truncate">{storeName}</p>
                )}
              </div>
            </div>

            {/* Price on the right */}
            <div className="text-right flex-shrink-0 ml-2">
              {(() => {
                const s = sellingPrice(
                  productDetails.price,
                  productDetails.markdown,
                );
                if (s === null) {
                  return (
                    <span className="font-semibold text-sm text-gray-400">
                      —
                    </span>
                  );
                }
                if (s === 0) {
                  return (
                    <span className="text-sm font-normal text-gray-500">
                      Contact Supplier for Pricing
                    </span>
                  );
                }
                return (
                  <span className="font-semibold text-sm">
                    ${formatPrice(s)}
                  </span>
                );
              })()}
            </div>
          </div>
        </AccordionTrigger>

        {/* TODO: remove arrow down, */}

        <AccordionContent className="px-4 pb-3 shadow-xl">
          {/* Rating */}
          {productDetails.average_rating && (
            <div className="flex items-center gap-1 mb-3 flex-wrap">
              <StarRating over={productDetails.average_rating} />
              <span className="text-xs sm:text-sm text-gray-600 ml-1">
                ({productDetails.average_rating.toFixed(1)})
              </span>
            </div>
          )}

          {/* Description (only if available) */}
          {productDetails.description && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-3">
              {productDetails.description}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Add to cart button */}
            <Button
              onClick={() =>
                addToCart?.({
                  id: Date.now(),
                  product_id: productDetails.id,
                  name: productDetails.name || "Unnamed Product",
                  price: productDetails.price || 0,
                  image: productDetails.media?.[0] || "",
                  quantity: 1,
                  store_id: productDetails.store_id || "",
                  store_name: storeName || "",
                })}
              className="flex-1 flex items-center justify-center gap-2"
              disabled={disableCart || !addToCart}
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden sm:inline">Add to Cart</span>
              <span className="sm:hidden">Add</span>
            </Button>

            {/* View in store link */}
            <Button mode="secondary">
              <Link
                to={`/store/${
                  slugify(storeName)
                }/products?product=${productDetails.id}`}
                className="flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                View
              </Link>
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ProductCardMini({
  productDetails,
  mainImageUrl,
  storeName,
  className,
  disableCart,
  addToCart,
}: CardProps) {
  const productUrl = `/product/${productDetails.id}`;

  return (
    <div
      className={`w-full border rounded-md h-full flex flex-col shadow-md hover:shadow-lg transition-shadow duration-300 ${className}`}
    >
      {/* Product Image */}
      <Link to={productUrl} className="block w-full">
        <div className="w-full h-48 bg-gray-100 overflow-hidden">
          {mainImageUrl
            ? (
              <ImageWithFallback
                key={mainImageUrl}
                src={mainImageUrl}
                alt={productDetails.name || "Product"}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <Image
                      className="w-12 h-12 text-gray-400"
                      aria-label="No image available"
                    />
                  </div>
                }
              />
            )
            : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <Image
                  className="w-12 h-12 text-gray-400"
                  aria-label="No image available"
                />
              </div>
            )}
        </div>
      </Link>

      {/* Header */}
      <div className="px-4 pt-3 pb-1 border-b">
        <div className="flex justify-between w-full items-start gap-2 min-w-0">
          {/* Product name and store */}
          <div className="text-left flex-1 min-w-0 overflow-hidden">
            <h3 className="font-medium text-md truncate">
              {productDetails.name || "Unnamed Product"}
            </h3>
            {storeName && (
              <Link
                to={`/store/${slugify(storeName)}`}
                className="text-xs text-blue-600 truncate hover:text-blue-800 hover:underline -mt-1"
              >
                {storeName}
              </Link>
            )}
          </div>

          {/* Price on the right */}
          {
            /* <div className="text-right flex-shrink-0 ml-2">
            <span className="font-semibold text-sm">
              $
              {formatPrice(
                productDetails.markdown ?? productDetails.price ?? 0
              )}
            </span>
          </div> */
          }
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3 flex-1 flex flex-col">
        {/* Rating */}
        {
          /* {productDetails.average_rating && (
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            <StarRating over={productDetails.average_rating} />
            <span className="text-xs sm:text-sm text-gray-600 ml-1">
              ({productDetails.average_rating.toFixed(1)})
            </span>
          </div>
        )} */
        }

        {/* Description (only if available) */}
        {productDetails.description && (
          <p className="text-sm text-gray-600 mt-2 mb-2 line-clamp-3">
            {productDetails.description}
          </p>
        )}
        {!productDetails.description && (
          <p className="text-sm text-gray-600 mt-2 mb-4 line-clamp-3"></p>
        )}

        {/* Content area that expands */}
        <div className="flex-1 flex flex-col">
          {/* Detailed price display in expanded view */}
          <div className="mb-1">
            <PriceDisplay
              price={productDetails.price}
              markdown={productDetails.markdown}
            />
          </div>
        </div>

        {/* Action buttons - pushed to bottom */}
        <div className="flex flex-col sm:flex-row gap-2 mt-auto">
          {/* Add to cart button */}
          <Button
            mode="primary"
            onClick={() =>
              addToCart?.({
                id: Date.now(),
                product_id: productDetails.id,
                name: productDetails.name || "Unnamed Product",
                price: productDetails.price || 0,
                image: productDetails.media?.[0] || "",
                quantity: 1,
                store_id: productDetails.store_id || "",
                store_name: storeName || "",
              })}
            className="flex-1 flex items-center justify-center gap-2"
            disabled={disableCart || !addToCart}
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">Add to Cart</span>
            <span className="sm:hidden">Add</span>
          </Button>

          {/* View in store link */}
          <Button mode="secondary">
            <Link
              to={`/product/${productDetails.id}`}
              className="flex items-center gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              View
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProductCardPromo({
  productDetails,
  mainImageUrl,
  storeName,
  className,
  fluid,
}: CardProps) {
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
  const isFavorited = favorites.some(
    (fav) => fav.product_id === productDetails.id,
  );
  const productUrl = storeName
    ? `/store/${slugify(storeName)}/products?product=${productDetails.id}`
    : `/product/${productDetails.id}`;

  const toggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFavorited) {
      removeFromFavorites(productDetails.id);
    } else {
      addToFavorites(productDetails.id);
    }
  };

  return (
    <Link
      to={productUrl}
      className={`bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow ${className} ${
        fluid
          ? "flex h-full min-h-0 w-full min-w-0 max-w-full flex-col"
          : "flex h-full min-h-0 w-[200px] min-w-[200px] shrink-0 flex-col"
      }`}
    >
      <div className="relative h-32 shrink-0 bg-gray-100">
        {mainImageUrl
          ? (
            <ImageWithFallback
              key={mainImageUrl}
              src={mainImageUrl}
              alt={productDetails.name || "Product"}
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <Image
                    className="w-8 h-8 text-gray-400"
                    aria-label="No image available"
                  />
                </div>
              }
            />
          )
          : (
            <div className="w-full h-full flex items-center justify-center">
              <Image
                className="w-8 h-8 text-gray-400"
                aria-label="No image available"
              />
            </div>
          )}
        {/* Favorite heart */}
        <button
          onClick={toggleFavorite}
          className="absolute top-2 right-2 z-10"
          title={isFavorited ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart
            className={`w-5 h-5 transition drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${
              isFavorited ? "fill-red-500 text-red-500" : "fill-none text-white"
            }`}
          />
        </button>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3">
        <h4 className="min-w-0 truncate text-sm font-medium leading-snug text-gray-900">
          {productDetails.name || "Unnamed Product"}
        </h4>
        {storeName && <p className="text-xs text-gray-500">by {storeName}</p>}
        {/* Fixed footprint so “contact supplier” / N/A matches numeric-price cards in a row */}
        <div className="mt-auto flex min-h-[2.25rem] min-w-0 flex-col justify-end pt-2">
          {(() => {
            const s = sellingPrice(
              productDetails.price,
              productDetails.markdown,
            );
            if (s === null || s === 0) {
              return (
                <p
                  className="truncate text-xs font-normal text-gray-500"
                  title="Contact supplier for pricing"
                >
                  Contact supplier for price
                </p>
              );
            }
            const md = productDetails.markdown;
            const pr = productDetails.price;
            const showSale = md != null &&
              pr != null &&
              md > 0 &&
              pr > md;
            if (showSale) {
              return (
                <div className="min-w-0 truncate">
                  <span className="text-sm font-medium text-green-600">
                    ${formatPrice(md)}
                  </span>
                  <span className="ml-2 text-xs text-gray-400 line-through">
                    ${formatPrice(pr)}
                  </span>
                </div>
              );
            }
            return (
              <p className="truncate text-sm font-medium text-gray-900">
                ${formatPrice(s)}
              </p>
            );
          })()}
        </div>
      </div>
    </Link>
  );
}

function ProductCardFull({
  productDetails,
  mainImageUrl,
  storeName,
  className,
  disableCart,
  addToCart,
}: CardProps) {
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
  const isFavorited = favorites.some(
    (fav) => fav.product_id === productDetails.id,
  );

  const toggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFavorited) {
      removeFromFavorites(productDetails.id);
    } else {
      addToFavorites(productDetails.id);
    }
  };

  const productName = productDetails.name || "Unnamed Product";

  // Filter to only get image URLs (exclude videos)
  const imageUrls = productDetails.media_urls
    ? Object.entries(productDetails.media_urls)
      .filter(([fileName]) => isProductMediaImageFileName(fileName))
      .map(([, url]) => url)
    : [];

  // State to manage current display image
  const [currentDisplayImage, setCurrentDisplayImage] = useState<
    string | undefined
  >(mainImageUrl);

  // Ref to track hover state
  const isHoveredRef = useRef(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cycleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentImageIndexRef = useRef(0);
  const imageUrlsRef = useRef<string[]>(imageUrls);
  const initialImageUrlRef = useRef<string | undefined>(undefined); // Track initial URL when hover begins
  const hasMovedRef = useRef(false); // Track if we've moved at least once (to avoid stopping immediately)

  // Update imageUrls ref when it changes
  useEffect(() => {
    imageUrlsRef.current = imageUrls;
  }, [imageUrls]);

  // Update current display image when mainImageUrl changes
  useEffect(() => {
    if (mainImageUrl) {
      setCurrentDisplayImage(mainImageUrl);
      // Find the index of mainImageUrl in imageUrls
      const mainIndex = imageUrlsRef.current.indexOf(mainImageUrl);
      if (mainIndex !== -1) {
        currentImageIndexRef.current = mainIndex;
      }
    } else if (imageUrlsRef.current.length > 0) {
      setCurrentDisplayImage(imageUrlsRef.current[0]);
      currentImageIndexRef.current = 0;
    }
  }, [mainImageUrl]);

  // Handle hover enter - start cycling after brief timeout
  const handleMouseEnter = () => {
    isHoveredRef.current = true;

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Clear any existing interval
    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
    }

    // Reset cycle tracking
    hasMovedRef.current = false;

    // Only cycle if there are multiple images
    if (imageUrlsRef.current.length > 1) {
      // Capture the current index and URL at the moment of hover enter
      const currentIndex = currentImageIndexRef.current >= 0 &&
          currentImageIndexRef.current < imageUrlsRef.current.length
        ? currentImageIndexRef.current
        : imageUrlsRef.current.indexOf(
          currentDisplayImage || mainImageUrl || "",
        );

      // Set the current index - this is our starting point
      currentImageIndexRef.current = currentIndex >= 0 ? currentIndex : 0;

      // Store the initial URL for comparison
      initialImageUrlRef.current = currentDisplayImage || mainImageUrl ||
        imageUrlsRef.current[currentImageIndexRef.current];

      hoverTimeoutRef.current = setTimeout(() => {
        if (isHoveredRef.current) {
          // Start cycling through images - stop when we return to starting image
          cycleIntervalRef.current = setInterval(() => {
            if (isHoveredRef.current && imageUrlsRef.current.length > 0) {
              // Move to next image
              currentImageIndexRef.current =
                (currentImageIndexRef.current + 1) %
                imageUrlsRef.current.length;
              const nextImageUrl =
                imageUrlsRef.current[currentImageIndexRef.current];
              setCurrentDisplayImage(nextImageUrl);
              hasMovedRef.current = true;

              // Check if we've moved and returned to the initial URL
              if (
                hasMovedRef.current &&
                nextImageUrl === initialImageUrlRef.current
              ) {
                // Stop cycling - we've completed one full cycle
                if (cycleIntervalRef.current) {
                  clearInterval(cycleIntervalRef.current);
                  cycleIntervalRef.current = null;
                }
                return;
              }
            }
          }, 1500); // Change image every 1.5s
        }
      }, 200); // Wait before starting to cycle
    }
  };

  // Handle hover exit - reset to main image
  const handleMouseLeave = () => {
    isHoveredRef.current = false;

    // Clear timeout and interval
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
    }

    // Reset cycle tracking
    hasMovedRef.current = false;

    // Reset to main image
    setCurrentDisplayImage(
      mainImageUrl ||
        (imageUrlsRef.current.length > 0 ? imageUrlsRef.current[0] : undefined),
    );
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
      }
    };
  }, []);

  if (!storeName) {
    return <ProductCardFullSkeleton />;
  }

  const productUrl = `/store/${
    slugify(storeName)
  }/products?product=${productDetails.id}`;

  const fullImageFallback = (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <Image
        className="w-12 h-12 text-gray-400"
        aria-label="No image available"
      />
    </div>
  );

  return (
    <div
      className={`w-full max-w-sm h-[420px] bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col relative ${className}`}
    >
      {/* Favorite heart toggle */}
      <button
        onClick={toggleFavorite}
        className="absolute top-2 right-2 z-10"
        title={isFavorited ? "Remove from favorites" : "Add to favorites"}
      >
        <Heart
          className={`w-5 h-5 transition drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${
            isFavorited ? "fill-red-500 text-red-500" : "fill-none text-white"
          }`}
        />
      </button>

      {/* Image container with hover-based cycling */}
      <div
        className="relative h-[250px] flex-shrink-0 overflow-hidden bg-gray-100"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Link
          className="block w-full h-full"
          to={productUrl}
        >
          {currentDisplayImage
            ? (
              <ImageWithFallback
                key={currentDisplayImage}
                src={currentDisplayImage}
                alt={productDetails.name || "Product"}
                className="h-full w-full object-contain transition-opacity duration-300"
                fallback={fullImageFallback}
              />
            )
            : fullImageFallback}
        </Link>
      </div>

      {/* Product Details */}
      <div className="p-4 flex flex-col flex-1 min-h-0">
        {/* Product Name with separate rating styling */}
        <Link
          to={productUrl}
          className="mb-4 min-h-[48px] flex items-start"
        >
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
            {productName}
            {productDetails.average_rating && (
              <span className="text-sm font-normal text-gray-500 ml-1">
                ({productDetails.average_rating.toFixed(1)})
              </span>
            )}
          </h3>
        </Link>

        {/* Price - using PriceDisplay component with fixed height */}
        <PriceDisplay
          price={productDetails.price}
          markdown={productDetails.markdown}
        />

        {/* Add to Cart Button */}
        <Button
          onClick={() =>
            addToCart?.({
              id: Date.now(),
              product_id: productDetails.id,
              name: productDetails.name || "Unnamed Product",
              price: productDetails.price || 0,
              image: currentDisplayImage || mainImageUrl ||
                productDetails.media?.[0] || "",
              quantity: 1,
              store_id: productDetails.store_id || "",
              store_name: storeName || "",
            })}
          className="w-full flex items-center justify-center gap-2 mt-auto"
          disabled={disableCart || !addToCart}
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="hidden sm:inline">Add to Cart</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>
    </div>
  );
}
